import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  TablePagination
} from '@mui/material'
import { toast } from 'react-hot-toast'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import MailIcon from '@mui/icons-material/Mail'
import RefreshIcon from '@mui/icons-material/Refresh'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import { useTheme } from '@mui/material/styles'
import DeleteMemberOrCancelRequest from './DeleteMeberOrCancelRequestDialog'

const PendingInvitationsTable = ({
  pendingMembers,
  handleRemoveMemberClick,
  handleResendInvitation
}) => {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedInvite, setSelectedInvite] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [pendingInvitations, setPendingInvitations] = useState([])
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleResndInvitation = (invite) => {
    handleResendInvitation(invite)
    toast.success(`Invitation to ${invite.email} resent`)
    closeMenu()
  }

  const handleCancelInvitation = () => {
    setOpenDeleteDialog(true)
  }

  const handleConfirmDelete = async () => {
    setOpenDeleteDialog(false)
    setPendingInvitations((prevInvitations) =>
      prevInvitations.filter((item) => String(item._id) !== String(selectedInvite._id))
    )
    handleRemoveMemberClick(selectedInvite._id)
    toast.success(`Invitation to ${selectedInvite.email} cancelled`)
    closeMenu()
  }

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false)
  }

  const openMenu = (event, invite) => {
    setAnchorEl(event.currentTarget)
    setSelectedInvite(invite)
  }

  const closeMenu = () => {
    setAnchorEl(null)
    setSelectedInvite(null)
  }

  const getPermissionIcon = (hasPermission) => {
    return hasPermission
      ? (
        <CheckCircleIcon sx={{ color: theme.palette.primary.main }} fontSize='small' />
        )
      : (
        <CancelOutlinedIcon color='error' fontSize='small' />
        )
  }

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr)
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()
    let hours = date.getHours()
    const minutes = date.getMinutes().toString().padStart(2, '0')
    const ampm = hours >= 12 ? 'PM' : 'AM'
    hours = hours % 12
    if (hours === 0) hours = 12
    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`
  }

  useEffect(() => {
    setPendingInvitations(pendingMembers || [])
  }, [pendingMembers])

  return (
    <Box sx={{ width: '100%' }}>
      {pendingInvitations && pendingInvitations.length > 0
        ? (
          <Paper elevation={0} sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
              <Table stickyHeader sx={{ minWidth: 650 }} aria-label='pending invitations table'>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Manage Sub Users</TableCell>
                    <TableCell align='center'>Manage Branding</TableCell>
                    <TableCell align='center'>Manage Plans</TableCell>
                    <TableCell align='center'>Manage Team Members</TableCell>
                    <TableCell>Invited By</TableCell>
                    <TableCell>Invited At</TableCell>
                    <TableCell align='right'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingInvitations
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((invite) => (
                      <TableRow
                        key={invite._id}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                        }}
                      >
                        <TableCell component='th' scope='row'>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ bgcolor: '#e2e8f0', color: '#64748b', mr: 2 }}>
                              <MailIcon size={20} />
                            </Avatar>
                            {invite.email}
                          </Box>
                        </TableCell>
                        <TableCell align='center'>
                          {getPermissionIcon(invite?.permissions?.manageSubUsers !== 'None')}
                        </TableCell>
                        <TableCell align='center'>
                          {getPermissionIcon(invite?.permissions?.manageBranding)}
                        </TableCell>
                        <TableCell align='center'>
                          {getPermissionIcon(invite?.permissions?.managePlans)}
                        </TableCell>
                        <TableCell align='center'>
                          {getPermissionIcon(invite?.permissions?.manageTeamMembers)}
                        </TableCell>
                        <TableCell align={invite.updatedBy ? 'left' : 'center'}>
                          {invite.createdBy ? invite.createdBy : '-'}
                        </TableCell>
                        <TableCell>{formatDateTime(invite.createdAt)}</TableCell>
                        <TableCell align='right'>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <IconButton size='small' onClick={(e) => openMenu(e, invite)}>
                              <MoreVertIcon size={16} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component='div'
              count={pendingInvitations.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </Paper>
          )
        : (
          <Box marginBlock={2} padding={2} border='1px solid #E4E4E5'>
            <Typography textAlign='center' width='100%' variant='body1'>
              No Pending Invitation Found
            </Typography>
          </Box>
          )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => handleResndInvitation(selectedInvite)}>
          <RefreshIcon size={16} style={{ marginRight: 8 }} />
          Resend invitation
        </MenuItem>
        <MenuItem
          onClick={() => handleCancelInvitation()}
          sx={{ color: 'error.main' }}
        >
          <CloseIcon size={16} style={{ marginRight: 8 }} />
          Cancel invitation
        </MenuItem>
      </Menu>

      {
        openDeleteDialog &&
          <DeleteMemberOrCancelRequest
            open={openDeleteDialog}
            onClose={handleCloseDeleteDialog}
            onDelete={handleConfirmDelete}
          />
      }
    </Box>
  )
}

export default PendingInvitationsTable
